import * as React from 'react';
import { observer } from 'mobx-react';
import { ResponsiveModal } from 'elc-responsive-modal';
import styled from 'styled-components';
import { RemoveIcon } from 'elc-icons';
import { ITranslationsCollection, translate, config } from 'elc-service';
import { ProductQuickViewContent, IProductQuickViewContentConfig } from './ProductQuickViewContent';
import { CartLimitReachedModal } from '../common/CartLimitReachedModal';
import { DEFAULT_NUMBER_OF_STARS } from '../../constants/ProductReview';
import {
    IProductAutoReplenish,
    IProductAutoReplenishOptions
} from '../product-auto-replenish/ProductAutoReplenish';
import { classes } from '../../utils/Classes';
import { MPP_ADD_TO_BAG } from '../../constants/DataTestIdAttributes';
import { ISizePicker, ISizePickerConfigs } from '../product-size-picker/ProductSizePicker';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { ISizePickerImages, IShortDescriptionConfig } from '../../interfaces/IProdcat';
import { IProductRatingConfig } from '../../../exported/product-rating/ProductRatingWrapper';
import { ADD_TO_COLLECTION } from '../../constants/CTATypes';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { ProductQuickViewController } from '../../controllers/ProductQuickViewController';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS } from '../../constants/ImageThumbnailCarouselSettings';
import { IShadePickerDropdown } from '../../interfaces/IShadePicker';
import { Product } from '../../domain/entities/Product';
import { Sku } from '../../domain/entities/Sku';

export interface IConfig {
    itemMaxQuantity?: number;
    hideSizeWhenSingular?: boolean;
    hideProductRating?: boolean;
    productGridHideReadReviewsLink?: boolean;
    enableLearnMoreLink?: boolean;
    autoReplenishOptions: number[];
    autoReplenishToggleType?: TOGGLE_TYPE;
    showToosTitle?: boolean;
    showToosMessage?: boolean;
    sizePickerImages?: ISizePickerImages;
    hideProductPricePerUnit?: boolean;
    showProductQuantitySelector?: boolean;
    className?: string;
    shortDescriptionLocation: IShortDescriptionConfig;
    enableAutoReplenishment?: boolean;
    sizePicker?: ISizePickerConfigs;
    customLabel?: string;
    popUpCTALabel?: string;
    pressedPopUpCTALabel?: string;
    enableNotifyMe?: boolean;
    ctaType?: string;
    autoReplenishment?: IProductAutoReplenishOptions;
    positionIndex?: number;
}

export interface IProductQuickView {
    config: IConfig;
    translations?: ITranslationsCollection;
    onShadeSelect(name: string): void;
    onSizeSelect(name: string): void;
    onProductQuickViewClose(): void;
    product: Product;
    selectedSku: Sku;
}

const Wrapper = styled.div`
    margin-top: 25px;
    display: inline-block;
    width: 100%;
`;

const ProductQuickViewWrapper = styled(ResponsiveModal)`
    padding: 0;
    width: 96%;
`;

const ProductQuickViewCloseButton = styled.button`
    cursor: pointer;
    float: right;
    border: none;
    outline: none;
    margin: 10px;
    [dir='rtl'] & {
        float: left;
    }
`;

@config([
    'itemMaxQuantity',
    'hideSizeWhenSingular',
    'sizePicker',
    'autoReplenishment',
    'ctaType',
    'installments'
])
@translate([
    'autoReplenishLabel',
    'subscribeLabel',
    'subscribeCopyLabel',
    'selectFrequencyLabel',
    'closeQuickShopAriaLabel',
    'notifyWhenAvailable'
])
@observer
export class ProductQuickView extends React.Component<IProductQuickView> {
    private productQuickViewWrapperClassNames: string = classes('product-quick-view-wrapper');
    private productQuickViewCloseButtonClassNames: string = classes(
        'product-quick-view-close-button'
    );
    private productQuickViewCloseButtonIconClassNames: string = classes(
        'product-quick-view-close-button-icon'
    );
    private productQuickViewContainerWrapperClassNames: string = classes(
        'product-quick-view-container-wrapper'
    );
    public static displayName: string;
    private viewController: ProductQuickViewController;

    constructor(props: IProductQuickView) {
        super(props);

        this.viewController = diContainer.get(serviceNames.productQuickViewController);
        this.viewController.setProduct(props.product);
        this.viewController.setSelectedSkuId(props.selectedSku.skuId);
    }

    private goToProduct = async () => {
        const { goToSPP } = this.viewController;
        const { positionIndex = 0 } = this.props.config;

        await goToSPP(positionIndex);
    };

    private handleShadeSelect = (name: string) => {
        this.props.onShadeSelect(name);
        this.viewController.selectShade(name);
    };

    private handleSizeSelect = (name: string) => {
        this.props.onSizeSelect(name);
        this.viewController.selectSize(name);
    };

    private closeProductQuickView = () => {
        this.props.onProductQuickViewClose();
    };

    public render() {
        const translations = this.props.translations as ITranslationsCollection;
        const { closeQuickShopAriaLabel } = translations;

        const {
            data,
            productUpdateQuantity,
            setAutoReplenishOption,
            setAutoReplenishSelection,
            toggleAutoReplenishSelection,
            largeImages,
            handleFavoriteClick
        } = this.viewController;

        const {
            product,
            sizes,
            shades,
            selectedAutoReplenishOption,
            isAutoReplenishSelected,
            productUrl,
            productQuickViewImageCarouselSettings,
            productQuickViewThumbnailsCarouselSettings,
            isImageCarouselDisabled,
            isThumbnailsCarouselEnabled
        } = data;

        const {
            displayName,
            subDisplayName,
            subHeader,
            shortDescription,
            quantity: currentQuantity,
            isShaded: isProductShaded,
            hasMultipleSizes,
            isSized
        } = product;

        const {
            enableLearnMoreLink,
            autoReplenishOptions,
            autoReplenishToggleType,
            showToosTitle,
            showToosMessage,
            hideProductPricePerUnit,
            showProductQuantitySelector,
            shortDescriptionLocation,
            enableAutoReplenishment,
            sizePicker,
            enableNotifyMe,
            popUpCTALabel,
            hideProductRating,
            pressedPopUpCTALabel,
            ctaType
        } = this.props.config;

        const selectedSku = this.props.selectedSku;

        const {
            skuId,
            inventoryStatus,
            isReplenishable,
            selectedShade,
            selectedSize
        } = selectedSku;

        const invStatusProps: IProductQuickViewContentConfig = {
            showToosTitle,
            showToosMessage,
            enableNotifyMe
        };
        const productId = selectedSku.partialParentProduct.productId;
        const { itemMaxQuantity, hideSizeWhenSingular, autoReplenishment } = this.props
            .config as IConfig;

        const customImages = this.viewController.getCustomSizeImages(
            ComponentTypes.PRODUCT_QUICKSHOP
        );
        const defaultImage = customImages.desktop.defaultImage;
        const customSizeImages = customImages.desktop.images;

        const productImageProps = {
            imgPath: defaultImage.src,
            altText: defaultImage.alt,
            productUrl,
            productDisplayName: displayName,
            onClick: this.goToProduct
        };

        const productNameProps = {
            displayName,
            productUrl,
            subDisplayName,
            subHeader,
            onProductNameClick: this.goToProduct
        };

        const productShortDescriptionProps = {
            productUrl,
            description: shortDescription,
            onProductDescriptionClick: this.goToProduct,
            enableLearnMoreLink
        };

        const productRatingProps = {
            isDisabled: true,
            numberOfStars: DEFAULT_NUMBER_OF_STARS,
            productLink: productUrl,
            onReadReviewsClick: this.goToProduct,
            productId,
            hideReviewsNumber: false,
            productGridHideReadReviewsLink: false
        };

        const productShadePickerProps: IShadePickerDropdown = {
            selectedShade: selectedShade,
            selectedHexValue: selectedShade.hexVal,
            shades,
            onShadeSelect: this.handleShadeSelect,
            productId: product.productId
        };

        const productSizePickerProps: ISizePicker = {
            sizes,
            selectedSize,
            onSelect: this.handleSizeSelect,
            hasMultipleSizes,
            isSized,
            hideSizeWhenSingular,
            productId,
            options: sizePicker ? sizePicker.product_quickshop : {}
        };

        const productQuantitySelectorProps = {
            config: {
                maxQuantity: itemMaxQuantity || 0,
                skuId,
                currentQuantity
            },

            onQuantitySelect: productUpdateQuantity
        };

        const productPriceRowProps = {
            selectedSku
        };

        const popUpCTAType = ctaType === ADD_TO_COLLECTION ? ctaType : undefined;

        const productAddToBagProps = {
            config: {
                skuId,
                inventoryStatus,
                quantity: currentQuantity,
                replenishment: selectedAutoReplenishOption,
                addToBagDataTestId: MPP_ADD_TO_BAG,
                showToosTitle,
                showToosMessage,
                enableNotifyMe,
                ctaType: popUpCTAType,
                translationsOverwrites: {
                    ctaButtonLabel: popUpCTALabel,
                    pressedCtaButtonLabel: pressedPopUpCTALabel
                }
            }
        };

        const autoReplenishmentProductQuickshop =
            autoReplenishment &&
            autoReplenishment.overrides &&
            autoReplenishment.overrides.productQuickshop;
        const autoReplenishmentConfig = {
            ...autoReplenishment,
            ...autoReplenishmentProductQuickshop
        };

        const productAutoReplenishProps: IProductAutoReplenish = {
            autoReplenishOptions,
            autoReplenishToggleType,
            onSelect: setAutoReplenishOption,
            toggleAutoReplenishSelection,
            setAutoReplenishSelection,
            selectedOption: selectedAutoReplenishOption,
            isSelected: isAutoReplenishSelected,
            translations,
            autoReplenishmentConfig
        };

        const thumbnailsCarouselSettings = {
            ...DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS,
            ...productQuickViewThumbnailsCarouselSettings
        };

        return (
            <Wrapper className={this.productQuickViewContainerWrapperClassNames}>
                <ProductQuickViewWrapper
                    className={this.productQuickViewWrapperClassNames}
                    isOpen={true}
                    title={displayName}
                    deactivateModal={this.closeProductQuickView}
                    onMaskClick={this.closeProductQuickView}
                    underlayClickExits={false}
                >
                    <ProductQuickViewCloseButton
                        aria-label={closeQuickShopAriaLabel}
                        value="close-quick-shop-button"
                        className={this.productQuickViewCloseButtonClassNames}
                        onClick={this.closeProductQuickView}
                        title={closeQuickShopAriaLabel}
                    >
                        <RemoveIcon className={this.productQuickViewCloseButtonIconClassNames} />
                    </ProductQuickViewCloseButton>

                    <ProductQuickViewContent
                        onCloseButtonClick={this.closeProductQuickView}
                        handleFavoriteClick={handleFavoriteClick}
                        product={product}
                        isProductShaded={isProductShaded}
                        productImageProps={productImageProps}
                        productNameProps={productNameProps}
                        productShortDescriptionProps={productShortDescriptionProps}
                        shortDescriptionLocation={shortDescriptionLocation}
                        productRatingProps={(productRatingProps as unknown) as IProductRatingConfig}
                        productShadePickerProps={productShadePickerProps}
                        productSizePickerProps={productSizePickerProps}
                        productQuantitySelectorProps={productQuantitySelectorProps}
                        productPriceRowProps={productPriceRowProps}
                        productAddToBagProps={productAddToBagProps}
                        hideProductRating={hideProductRating}
                        isProductReplenishable={isReplenishable}
                        productAutoReplenishProps={productAutoReplenishProps}
                        customSizeImages={customSizeImages}
                        largeImages={largeImages}
                        selectedSku={selectedSku}
                        autoReplenishOptions={autoReplenishOptions}
                        autoReplenishToggleType={autoReplenishToggleType}
                        hideProductPricePerUnit={hideProductPricePerUnit}
                        showProductQuantitySelector={showProductQuantitySelector}
                        enableAutoReplenishment={enableAutoReplenishment}
                        productQuickViewImageCarouselSettings={
                            productQuickViewImageCarouselSettings
                        }
                        thumbnailsCarouselSettings={thumbnailsCarouselSettings}
                        isImageCarouselDisabled={isImageCarouselDisabled}
                        isThumbnailsCarouselEnabled={isThumbnailsCarouselEnabled}
                        config={invStatusProps}
                        ctaType={ctaType}
                        translations={translations}
                    />
                </ProductQuickViewWrapper>
                <CartLimitReachedModal />
            </Wrapper>
        );
    }
}
